import { c as _c } from "react/compiler-runtime";
import type { CalendarDate } from '@internationalized/date';
import { GregorianCalendar } from '@internationalized/date';
import { useRangeCalendar } from '@react-aria/calendar';
import { useRangeCalendarState } from '@react-stately/calendar';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaState } from '../../../hooks/use-media-state';
import { calendarDateToDate, toCalendarDate, toRangeValue } from '../core/to-calendar-date';
import type { DateRangePickerProps } from './date-range-picker';
function createCalendar(identifier: string) {
  switch (identifier) {
    case 'gregory':
      return new GregorianCalendar();
    default:
      throw new Error(`Unsupported calendar ${identifier}`);
  }
}
export const useDateRangePickerConfig = t0 => {
  const $ = _c(56);
  let allowsNonContiguousRanges;
  let defaultValue;
  let gridLabel;
  let initialHideRangeMarkers;
  let max;
  let min;
  let onChange;
  let props;
  let renderDate;
  let value;
  let weekDayLabel;
  if ($[0] !== t0) {
    ({
      defaultValue,
      min,
      max,
      initialHideRangeMarkers,
      allowsNonContiguousRanges,
      onChange,
      weekDayLabel,
      gridLabel,
      renderDate,
      value,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = allowsNonContiguousRanges;
    $[2] = defaultValue;
    $[3] = gridLabel;
    $[4] = initialHideRangeMarkers;
    $[5] = max;
    $[6] = min;
    $[7] = onChange;
    $[8] = props;
    $[9] = renderDate;
    $[10] = value;
    $[11] = weekDayLabel;
  } else {
    allowsNonContiguousRanges = $[1];
    defaultValue = $[2];
    gridLabel = $[3];
    initialHideRangeMarkers = $[4];
    max = $[5];
    min = $[6];
    onChange = $[7];
    props = $[8];
    renderDate = $[9];
    value = $[10];
    weekDayLabel = $[11];
  }
  const isMobile = useMediaState("mobile");
  const [isUntouched, setIsUntouched] = useState(true);
  const [pinnedAnchorDate, setPinnedAnchorDate] = useState(null);
  const calendarRef = useRef(null);
  let t1;
  if ($[12] !== min) {
    t1 = toCalendarDate(min);
    $[12] = min;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  const minValue = t1;
  let t2;
  if ($[14] !== max) {
    t2 = toCalendarDate(max);
    $[14] = max;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const maxValue = t2;
  let t3;
  if ($[16] !== value) {
    t3 = toRangeValue(value);
    $[16] = value;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== defaultValue) {
    t4 = toRangeValue(defaultValue);
    $[18] = defaultValue;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const t5 = isMobile ? 1 : 2;
  let t6;
  if ($[20] !== t5) {
    t6 = {
      months: t5
    };
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== onChange) {
    t7 = range => {
      setPinnedAnchorDate(null);
      if (!range) {
        return onChange(null);
      }
      const {
        start,
        end
      } = range;
      onChange([calendarDateToDate(start), calendarDateToDate(end)]);
    };
    $[22] = onChange;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== allowsNonContiguousRanges || $[25] !== maxValue || $[26] !== minValue || $[27] !== props || $[28] !== t3 || $[29] !== t4 || $[30] !== t6 || $[31] !== t7) {
    t8 = {
      ...props,
      value: t3,
      defaultValue: t4,
      minValue,
      maxValue,
      visibleDuration: t6,
      allowsNonContiguousRanges,
      locale: "de-DE",
      createCalendar,
      onChange: t7
    };
    $[24] = allowsNonContiguousRanges;
    $[25] = maxValue;
    $[26] = minValue;
    $[27] = props;
    $[28] = t3;
    $[29] = t4;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const calendarState = useRangeCalendarState(t8);
  let t9;
  let t10;
  if ($[33] !== calendarState) {
    t10 = {
      ...calendarState,
      selectFocusedDate: _temp,
      commitSelection: _temp2
    };
    $[33] = calendarState;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  t9 = t10;
  const state = t9;
  let t11;
  if ($[35] !== props) {
    t11 = {
      ...props
    };
    $[35] = props;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const rangeCalendar = useRangeCalendar(t11, state, calendarRef);
  let t12;
  let t13;
  if ($[37] !== pinnedAnchorDate || $[38] !== state) {
    t12 = () => {
      if (!state.value && pinnedAnchorDate) {
        setPinnedAnchorDate(null);
        state.setAnchorDate(null);
      }
    };
    t13 = [state, pinnedAnchorDate];
    $[37] = pinnedAnchorDate;
    $[38] = state;
    $[39] = t12;
    $[40] = t13;
  } else {
    t12 = $[39];
    t13 = $[40];
  }
  useEffect(t12, t13);
  const t14 = isUntouched ? initialHideRangeMarkers : false;
  let t15;
  if ($[41] !== onChange) {
    t15 = nextValue => {
      setIsUntouched(false);
      onChange(nextValue);
    };
    $[41] = onChange;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== allowsNonContiguousRanges || $[44] !== gridLabel || $[45] !== isMobile || $[46] !== maxValue || $[47] !== minValue || $[48] !== pinnedAnchorDate || $[49] !== rangeCalendar || $[50] !== renderDate || $[51] !== state || $[52] !== t14 || $[53] !== t15 || $[54] !== weekDayLabel) {
    t16 = {
      ...rangeCalendar,
      minValue,
      maxValue,
      isMobile,
      state,
      calendarRef,
      pinnedAnchorDate,
      setPinnedAnchorDate,
      allowsNonContiguousRanges,
      initialHideRangeMarkers: t14,
      onChange: t15,
      weekDayLabel,
      gridLabel,
      renderDate
    };
    $[43] = allowsNonContiguousRanges;
    $[44] = gridLabel;
    $[45] = isMobile;
    $[46] = maxValue;
    $[47] = minValue;
    $[48] = pinnedAnchorDate;
    $[49] = rangeCalendar;
    $[50] = renderDate;
    $[51] = state;
    $[52] = t14;
    $[53] = t15;
    $[54] = weekDayLabel;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  return t16;
};
type DateValue = Parameters<DateRangePickerProps['onChange']>[0];
function _temp() {}
function _temp2() {}